"use client";

import React, { useState, useRef, useEffect } from "react";
import { usePathname } from "@/i18n/navigation";
import { useLocale } from "next-intl";

const LANGUAGES = [
  { code: "en", label: "US", native: "English", translated: "English" },
  { code: "hi", label: "IN", native: "हिंदी", translated: "Hindi" },
  { code: "ar", label: "SA", native: "العربية", translated: "Arabic" },
  { code: "es", label: "ES", native: "Español", translated: "Spanish" },
  { code: "fr", label: "FR", native: "Français", translated: "French" }
];

export function LanguageSwitcher() {
  const currentLocale = useLocale();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeLang = LANGUAGES.find(l => l.code === currentLocale) || LANGUAGES[0];

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const selectLanguage = (code: string) => {
    setOpen(false);
    if (code === currentLocale) return;
    // Use a full page reload so that Zoho SalesIQ (and other third-party scripts)
    // reinitialize cleanly on the new locale. Client-side navigation causes Zoho
    // to hide its float button via its internal SPA detection and it never re-shows.
    const newPath = `/${code}${pathname === "/" ? "" : pathname}`;
    window.location.href = newPath;
  };

  // Inline styles for bulletproof rendering, bypassing global CSS issues
  const switcherStyle: React.CSSProperties = {
    position: "relative",
    display: "inline-block",
  };

  const btnStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 14px",
    borderRadius: "999px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#053C61",
    backgroundColor: btnHover || open ? "#ffffff" : "rgba(255, 255, 255, 0.8)",
    border: "1px solid rgba(5, 60, 97, 0.12)",
    borderColor: btnHover || open ? "rgba(5, 60, 97, 0.25)" : "rgba(5, 60, 97, 0.12)",
    boxShadow: btnHover || open ? "0 4px 12px rgba(5, 60, 97, 0.08)" : "0 2px 8px rgba(5, 60, 97, 0.04)",
    backdropFilter: "blur(8px)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  };

  const dropdownStyle: React.CSSProperties = {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    zIndex: 9999,
    width: "220px",
    padding: "8px",
    backgroundColor: "#ffffff",
    border: "1px solid rgba(5, 60, 97, 0.12)",
    borderRadius: "16px",
    boxShadow: "0 10px 30px -10px rgba(5, 60, 97, 0.2), 0 4px 12px rgba(5, 60, 97, 0.05)",
    backdropFilter: "blur(16px)",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };

  const itemStyle = (isActive: boolean, isHovered: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 12px",
    border: "none",
    backgroundColor: isHovered ? "rgba(5, 60, 97, 0.05)" : (isActive ? "rgba(5, 60, 97, 0.02)" : "transparent"),
    borderRadius: "10px",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.15s ease",
  });

  const flagStyle: React.CSSProperties = {
    fontFamily: "var(--font-mono), monospace",
    fontSize: "11px",
    fontWeight: 700,
    color: "rgba(5, 60, 97, 0.7)",
    backgroundColor: "rgba(5, 60, 97, 0.06)",
    borderRadius: "4px",
    padding: "2px 5px",
    minWidth: "28px",
    textAlign: "center",
    marginRight: "12px",
    flexShrink: 0,
  };

  const namesStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  };

  const nameNativeStyle: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: 600,
    color: "#053C61",
    lineHeight: 1.2,
  };

  const nameTranslatedStyle: React.CSSProperties = {
    fontSize: "11px",
    color: "rgba(5, 60, 97, 0.5)",
    lineHeight: 1.2,
  };

  const checkStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    color: "#2D6BFF",
    marginLeft: "8px",
    flexShrink: 0,
  };

  return (
    <div className="lang-switcher" ref={containerRef} style={switcherStyle}>
      <button 
        type="button"
        className={`lang-btn ${open ? "is-open" : ""}`}
        onClick={toggleDropdown}
        onMouseEnter={() => setBtnHover(true)}
        onMouseLeave={() => setBtnHover(false)}
        aria-label="Select language"
        style={btnStyle}
      >
        <span className="lang-icon" style={{ display: "flex", alignItems: "center", color: "rgba(5, 60, 97, 0.6)" }}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="2" y1="12" x2="22" y2="12"/>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
        </span>
        <span className="lang-text" style={{ fontSize: "14px", fontWeight: 700 }}>{activeLang.code.toUpperCase()}</span>
        <span className="lang-chevron" style={{ display: "flex", alignItems: "center", transition: "transform 0.2s ease", color: "rgba(5, 60, 97, 0.5)", transform: open ? "rotate(180deg)" : "none" }}>
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </button>

      {open && (
        <div className="lang-dropdown" style={dropdownStyle}>
          {LANGUAGES.map((lang, idx) => {
            const isActive = lang.code === currentLocale;
            return (
              <button
                key={lang.code}
                type="button"
                className={`lang-item ${isActive ? "active" : ""}`}
                style={itemStyle(isActive, hoveredIdx === idx)}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  selectLanguage(lang.code);
                }}
              >
                <span className="lang-flag" style={flagStyle}>{lang.label}</span>
                <div className="lang-names" style={namesStyle}>
                  <span className="lang-name-native" style={nameNativeStyle}>{lang.native}</span>
                  <span className="lang-name-translated" style={nameTranslatedStyle}>{lang.translated}</span>
                </div>
                {isActive && (
                  <span className="lang-check" style={checkStyle}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
